// src/components/operationCenter/WidgetWrapper.tsx
// Common widget chrome with title bar, settings, and delete

import React from "react";
import { Settings, X, GripVertical } from "lucide-react";
import type { DashboardWidget, WidgetProps } from "@/types/dashboard";
import { WIDGET_REGISTRY } from "./widgets";
import { useDashboardStore } from "@/stores/dashboardStore";

interface WidgetWrapperProps {
  widget: DashboardWidget;
  isEditing: boolean;
  children?: React.ReactNode;
}

export const WIDGET_CARD =
  "h-full flex flex-col bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-xl overflow-hidden border border-slate-700/40 shadow-lg shadow-black/20 backdrop-blur-sm relative";

export const WIDGET_EMPTY =
  "h-full flex items-center justify-center text-slate-500 text-sm";

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  widget,
  isEditing,
  children,
}) => {
  const removeWidget = useDashboardStore((s) => s.removeWidget);
  const updateWidgetConfig = useDashboardStore((s) => s.updateWidgetConfig);
  const selectWidget = useDashboardStore((s) => s.selectWidget);
  const setWidgetSettingsOpen = useDashboardStore(
    (s) => s.setWidgetSettingsOpen
  );
  const definition = WIDGET_REGISTRY[widget.type];

  // Some widgets have their own internal header (like TopMovers, TweetFeed)
  const hasInternalHeader = definition?.hasInternalHeader ?? false;

  const handleSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectWidget(widget.id);
    setWidgetSettingsOpen(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Remove "${widget.title}" widget?`)) {
      removeWidget(widget.id);
    }
  };

  // Render widget content
  const WidgetComponent = definition?.component;
  const widgetProps: WidgetProps = {
    widget,
    isEditing,
    onConfigChange: (config) => updateWidgetConfig(widget.id, config),
  };

  // Floating edit controls - used by ALL widgets when editing
  const FloatingEditControls = isEditing ? (
    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-slate-900/80 backdrop-blur-sm rounded-lg p-1">
      <div className="drag-handle cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-200 transition-colors p-1">
        <GripVertical className="w-4 h-4" />
      </div>
      {definition?.configurable && (
        <button
          onClick={handleSettings}
          className="p-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors"
          title="Widget settings"
        >
          <Settings className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        onClick={handleDelete}
        className="p-1 rounded text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
        title="Remove widget"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  ) : null;

  // Minimal wrapper for widgets with internal headers
  if (hasInternalHeader) {
    return (
      <div className={WIDGET_CARD}>
        {/* Floating edit controls when in edit mode */}
        {FloatingEditControls}

        {/* Full-height content */}
        <div className="flex-1 min-h-0 overflow-hidden relative">
          {WidgetComponent ? (
            <WidgetComponent {...widgetProps} />
          ) : (
            <div className={WIDGET_EMPTY}>
              Widget not found
            </div>
          )}
          {children}
        </div>
      </div>
    );
  }

  // Standard wrapper with header bar - also uses floating edit controls
  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-xl overflow-hidden border border-slate-700/40 shadow-lg shadow-black/20 backdrop-blur-sm relative">
      {/* Floating edit controls when in edit mode */}
      {FloatingEditControls}

      {/* Header - matches standard DataCard title style */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        {/* Widget Icon */}
        {definition?.icon && (
          <definition.icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        )}

        {/* Title - standard format: uppercase, tracking-wide, text-xs */}
        <div className="flex-1 text-xs uppercase tracking-wide text-slate-300 truncate">
          {widget.title}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {WidgetComponent ? (
          <WidgetComponent {...widgetProps} />
        ) : (
          <div className={WIDGET_EMPTY}>
            Widget not found
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default WidgetWrapper;
